import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ResolutionCandidate } from "../../DocumentAnalysisAPI";
import CandidateTable from "./CandidateTable";
import { classLabel, fieldLabel } from "./ontologyValidation";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * The decision, asked in a dialog.
 *
 * A candidate table is wide — five columns of values, a match column, a button — and the places it
 * is needed are narrow: a card in a stepper, a 20% column beside a dashboard. Squeezed in there it
 * became a thing to scroll sideways rather than to read. A dialog is simply the right size for it,
 * and it also matches what is happening: this is a question that interrupts, answered once, closed.
 *
 * Above the table stands what the document says — the value that found these candidates. Without it
 * the reader sees three companies of the same name and no reason why they are on screen; with it
 * the question becomes the one they can actually answer: *the lease says "Müller Verwaltungs
 * GmbH" — which of these is that?*
 */

export interface DecisionSubject {
  /** The staged object's id; what a binding is recorded against. */
  extractedObjectId: string;
  term: string;
  className: string | null;
  candidates: ResolutionCandidate[];
  /** The key that found them, and the values it compared. */
  searchedKey?: string | null;
  searchedValues?: Record<string, any> | null;
  /** Where it came from, when several documents' decisions are asked in one place. */
  documentName?: string;
  boundObjectId?: string | null;
}

export default function DecisionDialog({
  subject,
  onClose,
  onDecide,
}: {
  subject: DecisionSubject | null;
  onClose: () => void;
  /** Resolves when the decision is recorded; `null` means none of the candidates. */
  onDecide: (subject: DecisionSubject, candidate: ResolutionCandidate | null) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const decide = async (candidate: ResolutionCandidate | null) => {
    if (!subject) return;

    setBusy(true);
    try {
      await onDecide(subject, candidate);
      onClose();
    } catch {
      // Caught, and deliberately silent here: whoever records the decision reports the failure in
      // its own way. What this must do is *not* close — a dialog that shuts on a failed write tells
      // the reader their answer was taken when it was not. It stays open so it can be tried again.
    } finally {
      setBusy(false);
    }
  };

  const searched = Object.keys(subject?.searchedValues ?? {});

  return (
    <Dialog open={!!subject} onOpenChange={(open) => (open ? null : onClose())}>
      {/* As wide as the screen allows: the table is the content, and five value columns beside a
          match column and a button do not fit a dialog sized for a form. */}
      <DialogContent className="max-h-[85vh] w-[95vw] max-w-[1400px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("documentAnalysisPanel.matching.askWhich", { count: subject?.candidates.length ?? 0 })}
          </DialogTitle>
        </DialogHeader>

        {subject ? (
          <div className="flex flex-col gap-3">
            {/* What the document says, which is the whole reason these rows are candidates. */}
            <div className="rounded-md border bg-muted/40 p-3">
              <div className="flex flex-wrap items-baseline gap-2 text-sm">
                <span className="font-semibold">{subject.term}</span>
                {/* What the thing is, not what the class is called: this dialog is opened from the
                    dashboard as well, where "LegalPerson" would tell a reader nothing. The technical
                    name stays reachable on hover for whoever wants it. */}
                <span className="text-xs text-muted-foreground" title={subject.className ?? ""}>
                  {classLabel(subject.className)}
                </span>
                {subject.documentName ? (
                  <span className="truncate text-xs text-muted-foreground" title={subject.documentName}>
                    {subject.documentName}
                  </span>
                ) : null}
              </div>

              {searched.length > 0 ? (
                <dl className="mt-1 grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-x-3 text-sm">
                  {searched.map((field) => (
                    <div key={field} className="contents">
                      {/* The field as the model names it for a reader, with the identifier on hover
                          for whoever is working on the model itself. */}
                      <dt className="truncate text-xs text-muted-foreground" title={field}>
                        {fieldLabel(subject.className, field)}
                      </dt>
                      <dd className="min-w-0 break-words font-medium">
                        {String((subject.searchedValues ?? {})[field])}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : null}

              {/* Only where the fields above did not already say it. A key named after the single
                  field it compares would otherwise appear twice in three lines, once in a private
                  notation. */}
              {subject.searchedKey && searched.length === 0 ? (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {t("documentAnalysisPanel.matching.foundVia", { key: subject.searchedKey })}
                </p>
              ) : null}
            </div>

            <CandidateTable
              className={subject.className}
              candidates={subject.candidates}
              boundObjectId={subject.boundObjectId}
              busy={busy}
              // Already said above, as what the document states; the table need not repeat it.
              statedFields={searched}
              onBind={(candidate) => void decide(candidate)}
              onReject={() => void decide(null)}
            />

            <p className="text-xs text-muted-foreground">
              {t("documentAnalysisPanel.matching.decisionMeaning")}
            </p>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
